import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  VENUE_BLOCKS_2026,
  decideKioskOutcome,
  hallOfBooths2026,
  kioskInputKind,
  lookupKioskInput,
  type BlockedReason,
  type EventDay,
  type KioskExhibitor,
} from '@nt-tokyo/shared';
import { formatJstDateLabel, formatJstTime, useDayView, useKioskData } from '@/lib/kiosk';
import { useSession } from '@/lib/authClient';
import ExhibitorCard, { EquipmentTags } from '@/components/kiosk/ExhibitorCard';
import KioskMenu from '@/components/kiosk/KioskMenu';
import KioskSettings from '@/components/kiosk/KioskSettings';
import KioskPendingList from '@/components/kiosk/KioskPendingList';

// 当日の出展者受付の端末 (10 インチ iPad 横置き・アクセスガイドで固定して、有人の受付に置く)。
// 出展者が配置番号 (C-12) か出展者番号 (42) を自分で入力し、表示された出展を見て受付ボタンを押す。
//
// - 番号を押すたびに該当する出展を表示する (確認画面は挟まない)
// - 日曜は、土曜に受付済みの両日出展者は受付不要 (開催概要の運用)。上部に大きく案内する
// - タイトルを 3 秒長押しすると管理者メニュー (受付の表・取消・未受付の一覧・端末の設定)
// - 回線が落ちても受付は続けられる (記録は端末にためて、つながったら送る: lib/kiosk.ts)

type Screen =
  | { type: 'input' }
  | { type: 'done'; exhibitor: KioskExhibitor; at: string; alreadyAt?: string }
  | { type: 'blocked'; exhibitor: KioskExhibitor; reason: BlockedReason };

type Panel = null | 'menu' | 'settings' | 'pending';

const LETTERS = VENUE_BLOCKS_2026.flatMap((b) => b.blocks);
const MAX_DIGITS = 4;
const IDLE_RESET_MS = 30 * 1000;
const DONE_RETURN_MS = 3000;
const LONG_PRESS_MS = 3000;

// iPad の「ホーム画面に追加」で Web アプリとして開けるようにする (Safari のアドレスバーが消えて全画面になる)。
// 管理画面全体を Web アプリ扱いにはしたくないので、受付画面を開いている間だけ head に足す。
// Safari は「ホーム画面に追加」を押した時点の head を読むので、受付画面から追加すれば効く。
function useKioskWebApp() {
  useEffect(() => {
    const added: HTMLElement[] = [];
    const add = (tag: 'link' | 'meta', attrs: Record<string, string>) => {
      const el = document.createElement(tag);
      for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
      document.head.appendChild(el);
      added.push(el);
    };
    add('link', { rel: 'manifest', href: '/kiosk.webmanifest' });
    add('link', { rel: 'apple-touch-icon', href: '/kiosk-icon-180.png' });
    add('meta', { name: 'apple-mobile-web-app-capable', content: 'yes' });
    add('meta', { name: 'mobile-web-app-capable', content: 'yes' });
    add('meta', { name: 'apple-mobile-web-app-title', content: '出展者受付' });
    add('meta', { name: 'apple-mobile-web-app-status-bar-style', content: 'default' });
    const prevTitle = document.title;
    document.title = 'NT東京2026 出展者受付';

    // ピンチでの拡大を止める。拡大されたまま放置されると、次の出展者が拡大された画面を見ることになる。
    // iOS の Safari は viewport の user-scalable=no を無視するので、iOS 独自の gesture イベントも止める。
    // maximum-scale=1 を入れ直すと、拡大されていた場合も等倍に戻る。
    const viewport = document.querySelector<HTMLMetaElement>('meta[name="viewport"]');
    const prevViewport = viewport?.getAttribute('content') ?? null;
    viewport?.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no');
    const block = (e: Event) => e.preventDefault();
    document.addEventListener('gesturestart', block, { passive: false });
    document.addEventListener('gesturechange', block, { passive: false });

    return () => {
      for (const el of added) el.remove();
      document.title = prevTitle;
      if (viewport && prevViewport !== null) viewport.setAttribute('content', prevViewport);
      document.removeEventListener('gesturestart', block);
      document.removeEventListener('gesturechange', block);
    };
  }, []);
}

export default function KioskPage() {
  useKioskWebApp();
  // RequireKiosk の内側なので、ここではログイン済み
  const { data: session } = useSession();
  const kiosk = useKioskData(session?.user?.id ?? 'unknown');
  const recordable = kiosk.today.day !== null;
  // 会期外 (前日の動作確認など) は 1 日目として表示だけする
  const day: EventDay = kiosk.today.day ?? 1;
  const { byId, summary } = useDayView(kiosk, day);
  const exhibitors = useMemo(() => kiosk.data?.exhibitors ?? [], [kiosk.data]);

  const [input, setInput] = useState('');
  const [screen, setScreen] = useState<Screen>({ type: 'input' });
  const [panel, setPanel] = useState<Panel>(null);
  const lastActivity = useRef(Date.now());

  const lookup = useMemo(() => lookupKioskInput(input, exhibitors, day), [input, exhibitors, day]);
  const matchState = lookup.type === 'match' ? byId.get(lookup.exhibitor.id) : undefined;
  const outcome = matchState ? decideKioskOutcome(matchState) : null;

  // 日曜に土曜受付済みの人の番号が出ている間は、表示した時刻を覚えておき、
  // 「戻る」を押したか放置で消えたときに「受付不要」として記録する (到着時刻の分析用)。
  // 打ち間違いの途中で一瞬出ただけのもの (打ち足した・消した) は記録しない。
  const notRequiredShown = useRef<{ exhibitorId: string; at: string; input: string } | null>(null);
  useEffect(() => {
    if (outcome?.type === 'not_required' && matchState) {
      if (notRequiredShown.current?.exhibitorId !== matchState.exhibitor.id) {
        notRequiredShown.current = { exhibitorId: matchState.exhibitor.id, at: kiosk.now().toISOString(), input };
      }
    } else {
      notRequiredShown.current = null;
    }
  }, [outcome?.type, matchState, kiosk, input]);

  const commitNotRequired = useCallback(() => {
    const shown = notRequiredShown.current;
    notRequiredShown.current = null;
    if (!shown || !recordable) return;
    kiosk.addRecord({
      exhibitorId: shown.exhibitorId,
      kind: 'not_required',
      source: 'self',
      inputValue: shown.input,
      recordedAt: shown.at,
    });
  }, [kiosk, recordable]);

  // キーは指が触れた瞬間 (pointerdown) に反応させる (InputScreen)。クリックの成立を待つと、
  // ごく短いタップが入力にならないことがある。完了画面は click で戻るので、戻したタップが
  // 次の画面のキーに化けることもない。
  const touch = () => {
    lastActivity.current = Date.now();
    return true;
  };

  const backToInput = useCallback(() => {
    lastActivity.current = Date.now();
    setScreen({ type: 'input' });
  }, []);

  // 放置: 入力途中なら消す / うまくいかなかった画面なら最初に戻す
  useEffect(() => {
    const t = setInterval(() => {
      if (panel) return;
      if (Date.now() - lastActivity.current < IDLE_RESET_MS) return;
      if (screen.type === 'input' && input !== '') {
        commitNotRequired();
        setInput('');
      } else if (screen.type === 'blocked') {
        backToInput();
      }
    }, 1000);
    return () => clearInterval(t);
  }, [panel, screen.type, input, commitNotRequired, backToInput]);

  // 完了画面は 3 秒で戻る
  useEffect(() => {
    if (screen.type !== 'done') return;
    const t = setTimeout(backToInput, DONE_RETURN_MS);
    return () => clearTimeout(t);
  }, [screen, backToInput]);

  // 配置番号の入力中 (C-12) に英字を押したら、数字は消して「A-」から入れ直す。数字を残して英字だけ
  // 入れ替えると、押し間違いに気づかないまま別の出展者 (C-12 → A-12) を受付してしまうため。
  // 出展者番号 (数字だけ) を入力中は英字キーを押せない (仕様)。配置番号に切り替えるときは
  // 「全部消す」で消してから英字を押す。
  const onLetter = (letter: string) => {
    if (!touch()) return;
    if (kioskInputKind(input) === 'number') return;
    setInput(`${letter}-`);
  };

  const onDigit = (d: string) => {
    if (!touch()) return;
    const digits = input.includes('-') ? input.slice(input.indexOf('-') + 1) : input;
    if (digits.length >= MAX_DIGITS) return;
    setInput(input + d);
  };

  const onBackspace = () => {
    if (!touch()) return;
    setInput(input.endsWith('-') ? '' : input.slice(0, -1));
  };

  const onClear = () => {
    if (!touch()) return;
    setInput('');
  };

  const onSubmit = () => {
    if (!touch() || !matchState || !outcome) return;
    const exhibitor = matchState.exhibitor;
    if (outcome.type === 'not_required') {
      commitNotRequired();
      setInput('');
      return;
    }
    if (outcome.type === 'blocked') {
      if (recordable) {
        kiosk.addRecord({ exhibitorId: exhibitor.id, kind: 'blocked', source: 'self', reason: outcome.reason, inputValue: input });
      }
      setScreen({ type: 'blocked', exhibitor, reason: outcome.reason });
    } else {
      // 同じ日の 2 回目は、受付とは別の種類で残す (受付数や取消の対象にしない)
      const kind = outcome.type === 'already' ? 'already' : 'checkin';
      const rec = recordable
        ? kiosk.addRecord({ exhibitorId: exhibitor.id, kind, source: 'self', inputValue: input })
        : null;
      setScreen({
        type: 'done',
        exhibitor,
        at: rec?.recordedAt ?? kiosk.now().toISOString(),
        alreadyAt: outcome.type === 'already' ? outcome.checkedInAt : undefined,
      });
    }
    setInput('');
  };

  if (kiosk.authError) {
    return (
      <Shell>
        <div className="flex flex-1 flex-col items-center justify-center gap-6 p-8 text-center">
          <div className="text-[44px] font-extrabold">スタッフにお声がけください</div>
          <div className="text-[20px] text-[#6b6b70]">（スタッフの方へ：ログインし直してください）</div>
          <a href="/sign-in?redirect=%2Fkiosk" className="rounded-2xl bg-[#1f4fd6] px-10 py-5 text-[26px] font-extrabold text-white">
            ログイン画面へ
          </a>
        </div>
      </Shell>
    );
  }

  if (!kiosk.data) {
    return (
      <Shell>
        <div className="flex flex-1 items-center justify-center text-[28px] text-[#6b6b70]">
          {kiosk.connection === 'offline' ? '通信できません。回線を確認してください' : '読み込み中…'}
        </div>
      </Shell>
    );
  }

  const sunday = day === 2 && recordable;

  return (
    <Shell>
      <Header
        dateLabel={formatJstDateLabel(kiosk.today.jstDate)}
        clock={formatJstTime(kiosk.today.date)}
        queueLength={kiosk.showPending ? kiosk.queueLength : 0}
        rejectedCount={kiosk.rejectedRecords.length}
        onLongPress={() => setPanel('menu')}
      />
      {!recordable && (
        <div className="bg-[#fff4e0] px-6 py-2 text-center text-[18px] font-bold text-[#a15c00]">
          会期外のためテスト表示です。受付しても記録されません。
        </div>
      )}

      {screen.type === 'input' && (
        <>
          {sunday && <SundayNotice />}
          <InputScreen
            compact={sunday}
            input={input}
            lookup={lookup.type}
            matched={matchState?.exhibitor}
            notRequired={outcome?.type === 'not_required'}
            wrongDayNote={
              outcome?.type === 'blocked' && outcome.reason === 'wrong_day' && matchState
                ? `${onlyDayLabel(matchState.exhibitor, kiosk.data.eventDates) || '別の日の出展'}です`
                : null
            }
            onLetter={onLetter}
            onDigit={onDigit}
            onBackspace={onBackspace}
            onClear={onClear}
            onSubmit={onSubmit}
          />
        </>
      )}

      {screen.type === 'done' && <DoneScreen screen={screen} onTap={backToInput} />}

      {screen.type === 'blocked' && (
        <BlockedScreen
          screen={screen}
          eventDates={kiosk.data.eventDates}
          onBack={() => {
            touch();
            backToInput();
          }}
        />
      )}

      {panel === 'menu' && (
        <KioskMenu
          kiosk={kiosk}
          day={day}
          byId={byId}
          done={summary.done}
          total={summary.total}
          onClose={() => {
            lastActivity.current = Date.now();
            setPanel(null);
          }}
          onOpenPending={() => setPanel('pending')}
          onOpenSettings={() => setPanel('settings')}
        />
      )}
      {panel === 'settings' && (
        <KioskSettings kiosk={kiosk} done={summary.done} total={summary.total} onBack={() => setPanel('menu')} />
      )}
      {panel === 'pending' && (
        <KioskPendingList kiosk={kiosk} summary={summary} recordable={recordable} onBack={() => setPanel('menu')} />
      )}
    </Shell>
  );
}

// 片日出展の「10月4日（日）のみの出展」。両日出展なら空文字。
function onlyDayLabel(
  exhibitor: KioskExhibitor,
  eventDates: { day1: string; day2: string | null } | null | undefined
): string {
  if (!eventDates) return '';
  const days = exhibitor.exhibitDays;
  const dates = [
    days.includes('saturday') ? eventDates.day1 : null,
    days.includes('sunday') ? eventDates.day2 : null,
  ].filter((d): d is string => !!d);
  return dates.length === 1 ? `${formatJstDateLabel(dates[0]!)}のみの出展` : '';
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="kiosk-root fixed inset-0 flex flex-col overflow-auto bg-[#f4f4f6] font-sans text-[#1d1d1f]">
      {children}
    </div>
  );
}

function Header({
  dateLabel,
  clock,
  queueLength,
  rejectedCount,
  onLongPress,
}: {
  dateLabel: string;
  clock: string;
  queueLength: number;
  // サーバが受け付けなかった記録の数 (管理者メニューで中身を確かめる)
  rejectedCount: number;
  onLongPress: () => void;
}) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const start = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(onLongPress, LONG_PRESS_MS);
  };
  const stop = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };
  return (
    <div className="flex shrink-0 items-center justify-between gap-3 border-b border-[#d9d9de] bg-white px-4 py-3 min-[900px]:px-9 min-[900px]:py-4">
      <div
        className="cursor-default text-[20px] font-extrabold tracking-wide min-[900px]:text-[26px]"
        onPointerDown={start}
        onPointerUp={stop}
        onPointerLeave={stop}
        onPointerCancel={stop}
        onContextMenu={(e) => e.preventDefault()}
      >
        NT東京2026 出展者受付
      </div>
      <div className="hidden text-[20px] text-[#6b6b70] sm:block min-[900px]:text-[22px]">{dateLabel}</div>
      <div className="flex items-center gap-3">
        {rejectedCount > 0 && (
          <span className="rounded-lg border border-[#f0b8b3] px-2 py-0.5 text-[14px] font-bold text-[#b3261e]">
            送れなかった {rejectedCount}
          </span>
        )}
        {queueLength > 0 && (
          <span className="rounded-lg border border-[#e7c48a] px-2 py-0.5 text-[14px] font-bold text-[#a15c00]">
            送信待ち {queueLength}
          </span>
        )}
        <span className="text-[22px] font-bold tabular-nums min-[900px]:text-[28px]">{clock}</span>
      </div>
    </div>
  );
}

// 日曜の常時案内。ほかの UI より先に目に入る大きさにする (白地・赤字、「土曜日」だけ青)。
function SundayNotice() {
  return (
    <div className="shrink-0 border-b border-[#d9d9de] bg-white px-4 pb-3 pt-2 text-center font-black leading-[1.12] text-[#b3261e]">
      {/* 横幅いっぱいに使いつつ、高さの低い画面 (Safari のアドレスバー付き) では縦も詰める */}
      <div className="text-[min(7.4vw,9vh,92px)]">
        <span className="text-[#1f4fd6]">土曜日</span>に受付済みの方は
      </div>
      <div className="text-[min(8.7vw,10.5vh,108px)]">本日の受付は不要です</div>
    </div>
  );
}

// テンキー・英字キーのイベント。指で触れた瞬間 (pointerdown) に反応させる (クリックの成立を
// 待つと、ごく短いタップが入力にならないことがある)。外付けキーボードの Enter/Space や
// VoiceOver のボタン実行は pointerdown を伴わず click だけが来るので、直前に pointerdown が
// 無かった click も受け付ける。指で触れたボタンでは、離したときの click が続けて来るので、
// それは (押していた時間に関係なく) 1 回だけ無視する (長押しで 2 回入らないように)。
function useKeyPress() {
  // 指で触れて、まだ click が来ていないボタン
  const pressed = useRef<EventTarget | null>(null);
  return (fn: () => void) => ({
    onPointerDown: (e: React.PointerEvent<HTMLButtonElement>) => {
      pressed.current = e.currentTarget;
      fn();
    },
    onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
      if (pressed.current === e.currentTarget) {
        pressed.current = null;
        return;
      }
      fn();
    },
  });
}

function InputScreen({
  compact,
  input,
  lookup,
  matched,
  notRequired,
  wrongDayNote,
  onLetter,
  onDigit,
  onBackspace,
  onClear,
  onSubmit,
}: {
  compact: boolean;
  input: string;
  lookup: 'empty' | 'partial' | 'none' | 'match';
  matched: KioskExhibitor | undefined;
  notRequired: boolean;
  // 出展日が違う (例: 日曜のみの出展者が土曜に入力した)。本人の情報なので押す前に出す。
  // 支払い前・キャンセルは後ろに並ぶ人にも見えるので、押す前にも後にも理由は出さない。
  wrongDayNote: string | null;
  onLetter: (l: string) => void;
  onDigit: (d: string) => void;
  onBackspace: () => void;
  onClear: () => void;
  onSubmit: () => void;
}) {
  const numberMode = kioskInputKind(input) === 'number';
  const keyProps = useKeyPress();
  const keyBase =
    'flex items-center justify-center rounded-2xl border-2 bg-white font-extrabold shadow-[0_2px_0_#cfcfd6] active:translate-y-[1px] active:shadow-none';
  // 英字キーと受付ボタンは、2 日目 (compact) でも 1 日目と同じ高さにする (押しやすさを優先)。
  // 上部の案内で減った高さは、伸び縮みする数字キーが吸収する。
  const letterSize = 'h-[56px] text-[26px] min-[900px]:h-[66px] min-[900px]:text-[30px] [@media(max-height:760px)]:min-[900px]:h-[56px]';
  // iPad (横幅 900px 以上) では、数字キーは右列の残りの高さいっぱいに伸び縮みさせる。
  // 固定の高さだと、画面の高さ (Safari のアドレスバーの有無・機種) によって、
  // 左の受付ボタンとテンキーの下端がずれたり、1 画面からはみ出したりするため。
  const digitSize = compact
    ? 'h-[60px] text-[28px] min-[900px]:h-auto min-[900px]:min-h-[44px] min-[900px]:text-[26px]'
    : 'h-[64px] text-[30px] min-[900px]:h-auto min-[900px]:min-h-[56px] min-[900px]:text-[36px]';

  let card: React.ReactNode;
  if (lookup === 'match' && matched) {
    card = (
      <ExhibitorCard exhibitor={matched} compact={compact}>
        {notRequired && (
          <div className="mt-1 text-[18px] font-extrabold leading-snug text-[#b3261e] min-[900px]:text-[20px]">
            土曜日に受付済みのため、本日の受付は不要です
          </div>
        )}
        {wrongDayNote && (
          <div className="mt-1 text-[18px] font-extrabold leading-snug text-[#a15c00] min-[900px]:text-[20px]">
            {wrongDayNote}
          </div>
        )}
      </ExhibitorCard>
    );
  } else if (lookup === 'none') {
    card = (
      <div className="flex min-h-[150px] items-center justify-center rounded-2xl border-2 border-dashed border-[#e7c48a] bg-[#fffaf0] p-4 text-center text-[22px] leading-relaxed text-[#a15c00]">
        該当する出展がありません。
        <br />
        番号をご確認ください。
      </div>
    );
  } else {
    card = (
      <div className="flex min-h-[150px] items-center justify-center rounded-2xl border-2 border-dashed border-[#d9d9de] p-4 text-center text-[22px] leading-relaxed text-[#a0a0a8]">
        {lookup === 'partial' ? '続けて入力してください' : '番号を入力すると、ここに出展が表示されます'}
      </div>
    );
  }

  const canSubmit = lookup === 'match' && !!matched;
  const buttonLabel = !canSubmit ? '受付する' : notRequired ? '受付は不要です（戻る）' : 'この出展で受付する';
  const buttonClass = !canSubmit
    ? 'bg-[#c9d9cf] text-white'
    : notRequired
      ? 'border-[3px] border-[#d9d9de] bg-white text-[#6b6b70]'
      : 'bg-[#127a3e] text-white shadow-[0_4px_0_#0b5129]';

  return (
    <div className={`flex flex-1 flex-col gap-5 p-4 min-[900px]:flex-row min-[900px]:gap-9 ${compact ? 'min-[900px]:px-9 min-[900px]:py-3' : 'min-[900px]:px-9 min-[900px]:py-7 [@media(max-height:760px)]:min-[900px]:py-4'}`}>
      <div className={`flex flex-col min-[900px]:w-[470px] min-[900px]:shrink-0 ${compact ? 'gap-2' : 'gap-4'}`}>
        <div className={`font-extrabold leading-snug ${compact ? 'text-[22px] min-[900px]:text-[20px]' : 'text-[26px] min-[900px]:text-[34px]'}`}>
          <span className="text-[#1f4fd6]">配置番号</span> または <span className="text-[#1f4fd6]">出展者番号</span> を
          <br className={compact ? 'hidden' : 'hidden min-[900px]:inline'} />
          入力してください
        </div>
        <div
          className={`flex items-center justify-center rounded-2xl border-[3px] bg-white font-extrabold tracking-wider tabular-nums ${
            input ? (lookup === 'none' ? 'border-[#a15c00]' : 'border-[#1f4fd6]') : 'border-[#d9d9de]'
          } ${compact ? 'h-[76px] text-[52px] min-[900px]:h-[64px] min-[900px]:text-[48px]' : 'h-[100px] text-[64px] min-[900px]:h-[130px] min-[900px]:text-[88px] [@media(max-height:760px)]:min-[900px]:h-[100px] [@media(max-height:760px)]:min-[900px]:text-[72px]'}`}
        >
          {input || <span className="text-[26px] font-semibold tracking-normal text-[#c2c2c8] min-[900px]:text-[36px]">例：C-12 または 42</span>}
        </div>
        {card}
        <button
          type="button"
          disabled={!canSubmit}
          onClick={onSubmit}
          className={`mt-auto flex items-center justify-center rounded-2xl font-extrabold ${buttonClass} ${
            'h-[96px] text-[30px] min-[900px]:h-[110px] min-[900px]:text-[36px] [@media(max-height:760px)]:min-[900px]:h-[90px]'
          }`}
        >
          {buttonLabel}
        </button>
      </div>

      <div className="flex flex-1 flex-col gap-2 min-[900px]:min-h-0">
        <div className="text-[16px] font-bold text-[#6b6b70]">配置番号の英字</div>
        <div className="grid grid-cols-7 gap-2 min-[900px]:gap-2.5">
          {LETTERS.map((l) => (
            <button
              key={l}
              type="button"
              disabled={numberMode}
              {...keyProps(() => onLetter(l))}
              className={`${keyBase} ${letterSize} ${
                numberMode ? 'border-[#e5e5ea] bg-[#f7f7f9] text-[#cfcfd4] shadow-none' : 'border-[#d9d9de]'
              }`}
            >
              {l}
            </button>
          ))}
        </div>
        <div className="mt-1 text-[16px] font-bold text-[#6b6b70]">数字</div>
        <div className="grid grid-cols-3 gap-2 min-[900px]:flex-1 min-[900px]:grid-rows-4 min-[900px]:gap-2.5">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
            <button key={d} type="button" {...keyProps(() => onDigit(d))} className={`${keyBase} ${digitSize} border-[#d9d9de]`}>
              {d}
            </button>
          ))}
          <button type="button" {...keyProps(onClear)} className={`${keyBase} ${digitSize} border-[#d9d9de] !text-[20px] text-[#6b6b70]`}>
            全部消す
          </button>
          <button type="button" {...keyProps(() => onDigit('0'))} className={`${keyBase} ${digitSize} border-[#d9d9de]`}>
            0
          </button>
          <button type="button" {...keyProps(onBackspace)} className={`${keyBase} ${digitSize} border-[#d9d9de] !text-[20px] text-[#6b6b70]`}>
            1字消す
          </button>
        </div>
      </div>
    </div>
  );
}

function DoneScreen({ screen, onTap }: { screen: Extract<Screen, { type: 'done' }>; onTap: () => void }) {
  const hall = hallOfBooths2026(screen.exhibitor.boothNumbers);
  const booth = screen.exhibitor.boothNumbers.join(', ');
  return (
    <button
      type="button"
      onClick={onTap}
      className="flex flex-1 flex-col items-center justify-center gap-5 bg-[#e7f5ec] p-6 text-center"
    >
      <div className="flex h-[120px] w-[120px] items-center justify-center rounded-full bg-[#127a3e] text-[80px] font-extrabold text-white min-[900px]:h-[150px] min-[900px]:w-[150px] min-[900px]:text-[96px]">
        ✓
      </div>
      <div className="text-[44px] font-extrabold min-[900px]:text-[58px]">{screen.alreadyAt ? '受付済みです' : '受付しました'}</div>
      <div className="text-[24px] text-[#3a3a40] tabular-nums min-[900px]:text-[32px]">
        {screen.exhibitor.exhibitorName} ／{' '}
        {screen.alreadyAt ? `本日 ${formatJstTime(screen.alreadyAt)} に受付済み` : formatJstTime(screen.at)}
      </div>
      {screen.alreadyAt ? (
        <div className="text-[24px] min-[900px]:text-[30px]">もう一度受付する必要はありません</div>
      ) : (
        <>
          {booth && (
            <div className="text-[24px] min-[900px]:text-[30px]">
              ブースは{' '}
              <b className="text-[#1f4fd6]">
                {hall ? `${hall} ` : ''}
                {booth}
              </b>{' '}
              です
            </div>
          )}
          <EquipmentTags exhibitor={screen.exhibitor} large />
        </>
      )}
      <div className="h-[10px] w-[420px] max-w-full overflow-hidden rounded-full bg-[#cfe8d8]">
        <div
          className="h-full bg-[#127a3e]"
          style={{ animation: `kiosk-countdown ${DONE_RETURN_MS}ms linear forwards` }}
        />
      </div>
      <div className="text-[18px] text-[#6b6b70] min-[900px]:text-[20px]">画面をタップすると最初に戻ります</div>
    </button>
  );
}

function BlockedScreen({
  screen,
  eventDates,
  onBack,
}: {
  screen: Extract<Screen, { type: 'blocked' }>;
  eventDates: { day1: string; day2: string | null } | null;
  onBack: () => void;
}) {
  const wrongDay = screen.reason === 'wrong_day';
  const daysLabel = wrongDay ? onlyDayLabel(screen.exhibitor, eventDates) : '';
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-6 bg-[#fdecea] p-6 text-center">
      <div className="flex h-[120px] w-[120px] items-center justify-center rounded-full bg-[#b3261e] text-[80px] font-extrabold text-white min-[900px]:h-[150px] min-[900px]:w-[150px] min-[900px]:text-[96px]">
        !
      </div>
      <div className="text-[40px] font-extrabold min-[900px]:text-[58px]">
        {wrongDay ? '出展日が異なります' : 'スタッフにお声がけください'}
      </div>
      {wrongDay ? (
        <>
          <div className="text-[24px] text-[#3a3a40] min-[900px]:text-[32px]">
            {screen.exhibitor.exhibitorName}
            {daysLabel ? ` ／ ${daysLabel}` : ''}
          </div>
          <div className="text-[24px] min-[900px]:text-[30px]">近くのスタッフにお声がけください。</div>
        </>
      ) : (
        <div className="text-[24px] leading-relaxed min-[900px]:text-[30px]">
          この端末では受付を完了できませんでした。
          <br />
          近くのスタッフにお声がけください。
        </div>
      )}
      <button
        type="button"
        onClick={onBack}
        className="flex h-[90px] min-w-[360px] items-center justify-center rounded-2xl border-[3px] border-[#d9d9de] bg-white px-10 text-[28px] font-extrabold text-[#3a3a40] min-[900px]:h-[100px] min-[900px]:text-[34px]"
      >
        最初の画面に戻る
      </button>
    </div>
  );
}
